import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Activity, Calendar, Heart, Download, CheckCircle, Users, TrendingUp, Clock, Package, Wrench, FileText, Camera, MessageSquare, Shield, Cloud, Smartphone, Monitor } from 'lucide-react';
import { toast } from 'sonner';

export const TalleresPage = () => {
  const handleDownload = () => {
    toast.success('Preparando descarga de VERSO Talleres...');
  };

  const problems = [
    { icon: FileText, title: 'Logística y Eficiencia', description: 'Reemplaza órdenes de servicio en papel o Excel, reduciendo errores y retrasos' },
    { icon: Shield, title: 'Calidad y Garantías', description: 'Mejora procesos de reparación para disminuir garantías excesivas' },
    { icon: Package, title: 'Gestión de Repuestos', description: 'Agiliza la consecución y cotización de repuestos con proveedores' },
    { icon: Calendar, title: 'Planificación Inteligente', description: 'Gestiona la planificación del taller y citas según la ocupación' },
    { icon: MessageSquare, title: 'Transparencia al Cliente', description: 'Permite al cliente ver el estado de su vehículo en tiempo real' },
    { icon: Wrench, title: 'Mantenimiento Preventivo', description: 'Facilita la creación y seguimiento de planes de mantenimiento' },
    { icon: Activity, title: 'Asesoría Técnica', description: 'Accede a planos y asesoría para reparaciones complejas' },
    { icon: Cloud, title: 'Base de Datos en la Nube', description: 'Crea un historial completo del vehículo centralizado' }
  ];

  const modules = [
    {
      title: 'Órdenes de Servicio Inteligentes',
      icon: FileText,
      features: [
        'Registro completo de cliente, vehículo y labores',
        'Inventario fotográfico automático al ingreso',
        'Asignación de labores a mecánicos con estados',
        'Gestión de servicios estandarizada',
        'Solicitud de repuestos desde base de datos'
      ]
    },
    {
      title: 'Diagnóstico y Evidencia',
      icon: Camera,
      features: [
        'Definición de servicios con evidencia fotográfica',
        'Especificación de repuestos requeridos',
        'Adjuntar evidencia del problema',
        'Solicitud automática de cotización',
        'Base de datos de servicios'
      ]
    },
    {
      title: 'Cotización Automatizada',
      icon: TrendingUp,
      features: [
        'Gestión con proveedores via web',
        'Envío de cotización al cliente',
        'Cliente visualiza diagnóstico con evidencia',
        'Autorización digital del cliente',
        'Comparación de precios entre proveedores'
      ]
    },
    {
      title: 'Control de Calidad',
      icon: Shield,
      features: [
        'Listas de chequeo vinculadas',
        'Actualización de estados',
        'Control de entrega de repuestos',
        'Evidencias fotográficas de resultados',
        'Chequeo de calidad del servicio'
      ]
    },
    {
      title: 'Gestión de Datos',
      icon: Users,
      features: [
        'Base de datos de vehículos y clientes',
        'Servicios estandarizados con precios',
        'Planes de mantenimiento personalizables',
        'Integración con SIIGO (opcional)',
        'Sincronización bidireccional'
      ]
    },
    {
      title: 'Vista Web para Clientes',
      icon: Monitor,
      features: [
        'Cliente ve estado en tiempo real',
        'Visualización de diagnóstico',
        'Aprobación de cotizaciones online',
        'Acceso a evidencia y resultados',
        'Historial completo de servicios'
      ]
    }
  ];

  const additionalFeatures = [
    'Módulo de Citas con gestión de ocupación',
    'Plataforma de Proveedores integrada',
    'Módulo de Técnicos para asesorías',
    'Contabilidad: Inventario y ventas',
    'Módulo de Peritajes personalizable',
    'Vista Desktop para computadores'
  ];

  const stats = [
    { value: '+15k', label: 'Talleres Activos' },
    { value: '40%', label: 'Aumento Productividad' },
    { value: '100%', label: 'Digital' },
    { value: '24/7', label: 'Disponibilidad' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-cyan-900/20 to-slate-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/30 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
                <Wrench className="text-cyan-400" size={16} />
                <span className="text-cyan-300 text-sm font-medium">VERSO TALLERES</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Digitaliza tu taller
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  de principio a fin
                </span>
              </h1>
              <p className="text-gray-400 text-xl mb-8 leading-relaxed">
                Software completo para talleres mecánicos que optimiza procesos, mejora la calidad, 
                facilita la comunicación con clientes y crea un historial vehicular en la nube.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={handleDownload}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
                >
                  <Download className="mr-2" size={20} />
                  Descargar VERSO Talleres
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 px-8 py-6 text-lg rounded-xl"
                >
                  <Smartphone className="mr-2" size={20} />
                  Ver Demo
                </Button>
              </div>

              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>✓ Prueba gratis 30 días</span>
                <span>•</span>
                <span>✓ Sin tarjeta requerida</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-cyan-500/20 p-6 text-center hover:border-cyan-500/40 transition-all">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problems Solved */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Problemáticas que VERSO Talleres Soluciona</h2>
            <p className="text-gray-400 text-lg">Desarrollado para los desafíos del taller moderno</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, index) => {
              const Icon = problem.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-cyan-500/20 p-6 hover:border-cyan-500/40 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-cyan-400" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{problem.title}</h3>
                  <p className="text-gray-400 text-sm">{problem.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-black to-slate-900"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Módulos Completos y Potentes</h2>
            <p className="text-gray-400 text-lg">Todo en una sola plataforma</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => {
              const Icon = module.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-cyan-500/20 p-6 hover:border-cyan-500/40 transition-all duration-300">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                      <Icon className="text-cyan-400" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-white">{module.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {module.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="text-cyan-400 mt-0.5 flex-shrink-0" size={14} />
                        <span className="text-gray-400">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <div className="mt-12">
            <Card className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30 backdrop-blur-sm p-8">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Características Adicionales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {additionalFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckCircle className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30 backdrop-blur-sm p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Únete a los +15,000 Talleres que Usan VERSO
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Aumenta tu productividad hasta un 40% con servicio profesional
            </p>
            <Button 
              onClick={handleDownload}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-12 py-6 text-lg rounded-xl"
            >
              <Download className="mr-2" size={20} />
              Descargar VERSO Talleres Gratis
            </Button>
            <p className="text-gray-400 text-sm mt-4">
              30 días de prueba • Sin tarjeta de crédito
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};
